/**
 * StatusBarTapZone — invisible overlay covering the status bar area.
 *
 * Tapping anywhere in the status bar scrolls the frontmost content to top.
 * This is the v1 "StatusBarTapBox" pattern, required because:
 * - iOS native `statusTap` only works reliably with one scroll container
 * - With `overlaysWebView: true` the WebView receives touches in the
 *   status bar area, so the native status-bar-tap gesture won't fire
 * - The invisible overlay ensures consistent behaviour on native AND
 *   mobile Safari (PWA / dev-server testing)
 *
 * Only rendered on mobile viewports.
 * Position: fixed, full width, status bar height + 10px for touch target.
 * z-index MAX_SAFE_INTEGER: above everything including dev overlays.
 */

import { useBreakpoint } from "@/shared/hooks/useBreakpoint"

interface StatusBarTapZoneProps {
  onTap: () => void
}

export function StatusBarTapZone({ onTap }: StatusBarTapZoneProps) {
  const { isMobile } = useBreakpoint()

  if (!isMobile) return null

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: "calc(var(--status-bar-height, 20px) + 10px)",
        zIndex: 2147483647,
        // Nearly-transparent background ensures iOS WebKit registers touch events.
        // Fully transparent elements are sometimes ignored by the hit-testing engine.
        background: "rgba(0,0,0,0.001)",
        WebkitTapHighlightColor: "transparent",
        cursor: "pointer",
        touchAction: "manipulation",
      }}
      onClick={onTap}
    />
  )
}
